import {
  ConsentContext,
  PrivacyApi,
  PrivacyEvent,
} from '@accedo/one-plugin-sdk';
import { proxy, useSnapshot } from 'valtio';

import { devtoolsApi } from '#/dev/devtools';
import { FeaturePrivacyProps } from '#/service/config/features.types';

export type ExtendedPrivacyType = {
  apiRef: PrivacyApi;
  properties: FeaturePrivacyProps;
};

export interface PrivacyStore {
  privacyPlugin: ExtendedPrivacyType | undefined;
  savedConsents?: ConsentContext;
}

type EventListenerType = {
  event: PrivacyEvent;
  callback: (data?: any) => void;
};

const eventListeners: EventListenerType[] = [];

export const privacyStore = proxy<PrivacyStore>({
  privacyPlugin: undefined,
});

export const privacyActions = {
  save(plugin: ExtendedPrivacyType) {
    privacyStore.privacyPlugin = plugin;
  },
  addEventListener(event: PrivacyEvent, callback: (data?: any) => void) {
    if (
      !eventListeners.find(
        (eventListener) => eventListener.callback === callback
      )
    ) {
      eventListeners.push({ event, callback });
      privacyStore.privacyPlugin?.apiRef.addEventListener(event, callback);
    }
  },
  removeEventListener(event: PrivacyEvent, callback: (data?: any) => void) {
    privacyStore.privacyPlugin?.apiRef?.removeEventListener(event, callback);
  },
  toggleFloatingButton(show?: boolean) {
    // If button is disabled we are never going to show it in the app but if it's not disabled
    // we can show or hide in different cases like when we are on the playback page.
    const buttonEnabled =
      !privacyStore.privacyPlugin?.properties.disableFloatingButton;
    const floatingButton =
      privacyStore.privacyPlugin?.apiRef.getFloatingButton?.();

    if (floatingButton) {
      floatingButton.style.display = show && buttonEnabled ? 'initial' : 'none';
    }
  },
  showCpUi() {
    privacyStore.privacyPlugin?.apiRef.showPrivacyManager();
  },
  saveConsents(data: ConsentContext) {
    privacyStore.savedConsents = data;
  },
  getConsents() {
    return privacyStore.savedConsents;
  },
  reset() {
    for (let index = eventListeners.length - 1; index >= 0; index--) {
      const { event, callback } = eventListeners[index] || {};

      if (event && callback) {
        privacyActions.removeEventListener(event, callback);
      }
    }
  },
};

export const usePrivacyStore = () => useSnapshot(privacyStore) as PrivacyStore;

devtoolsApi.setStore('privacy', privacyStore, privacyActions);
